/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { BusyTimes, UserBusyTimes } from "@microsoft/shifts-models-data";
import { NetworkService } from "sh-services";
import { DataProvider } from "sh-services/dataproviders/DataProvider";

import { CrossLocationShiftDataProviderParams } from "./CrossLocationShiftDataProviderParams";

/**
 * The cross-location shift data provider.
 */
export class CrossLocationShiftDataProvider extends DataProvider<UserBusyTimes[]> {
    /**
     * Instantiates a new instance of the CrossLocationShiftDataProvider.
     * @param params The parameters.
     */
    constructor(private readonly params: CrossLocationShiftDataProviderParams) {
        super();
    }

    /**
     * Gets data from memory or undefined.
     * @returns The data if it's found in memory.
     */
    public async getDataFromMemory(): Promise<UserBusyTimes[] | undefined> {
        // Memory only stores view models, so returning undefined.
        return Promise.resolve(undefined);
    }

    /**
     * Return data if it's found in the database (otherwise return undefined).
     * @returns The data if it's found in database.
     */
    public async getDataFromDatabase(): Promise<UserBusyTimes[]> {
        const { database, endTimestampMs, startTimestampMs, teamId } = this.params;

        const usersShifts = await database.getCrossLocationShifts({
            endTimestampMs,
            startTimestampMs,
            teamId
        });

        return usersShifts;
    }

    /**
     * Gets data from network call.
     * @returns The data from the network call.
     */
    public async getDataFromNetwork(): Promise<UserBusyTimes[]> {
        const { endTimestampMs, startTimestampMs, teamId, tenantId } = this.params;
        let url = `/api/tenants/${tenantId}/teams/${teamId}/busytime`;
        const urlSignature = "/api/tenants/{tenantId}/teams/{teamId}/busytime";
        const startTime = this.toIsoDateTime(startTimestampMs);
        const endTime = this.toIsoDateTime(endTimestampMs);

        url += `?startTime=${startTime}&endTime=${endTime}`;

        const { data } = await NetworkService.sendHttpRequestToStaffHubService<BusyTimes>({
            method: "GET",
            url,
            urlSignature
        });

        return data.value ?? [];
    }

    /**
     * Sets data in memory.
     * @param data The data to set in memory.
     */
    public async setDataInMemory(data: UserBusyTimes[]): Promise<void> {
        this.params.setDataInMemory(data);
    }

    /**
     * Sets data in database.
     * @param data The data to set in database.
     */
    public async setDataInDatabase(data?: UserBusyTimes[]): Promise<void> {
        if (data) {
            const { database, endTimestampMs, startTimestampMs, teamId } = this.params;

            await database.setCrossLocationShifts({
                endTimestampMs,
                startTimestampMs,
                teamId
            }, data);
        }
    }

    /**
     * Not supported.
     */
    public async saveDataToNetwork(): Promise<UserBusyTimes[]> {
        return [];
    }

    /**
     * Converts given timestamp in milliseconds to ISO date-time string.
     * @param timestampMs The timestamp in milliseconds.
     * @returns The ISO date-time string.
     */
    private toIsoDateTime(timestampMs: number): string {
        return new Date(timestampMs).toISOString();
    }
}
